import { Helmet } from "react-helmet";
import { Link } from "wouter";

const HowItWorks = () => {
  return (
    <>
      <Helmet>
        <title>How It Works | SurpriseTable</title>
        <meta
          name="description"
          content="Learn how SurpriseTable works. Book a surprise meal, enjoy premium dining, and save money on your culinary adventures."
        />
      </Helmet>

      <section className="bg-white py-16 transition-colors duration-200 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h1 className="mb-4 font-serif text-4xl font-bold text-gray-800 dark:text-white">
              How SurpriseTable Works
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-300">
              Discover how our platform connects you with amazing dining
              experiences at a fixed price.
            </p>
          </div>

          <div className="mx-auto max-w-5xl">
            {/* Step 1 */}
            <div className="mb-16 md:flex md:items-center">
              <div className="mb-8 md:mb-0 md:w-1/2 md:pr-12">
                <div className="rounded-full bg-primary/10 p-3 inline-block mb-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
                    1
                  </span>
                </div>
                <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                  Book Your Surprise Meal
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  Select your preferred date, time, and location. Let us know about any
                  dietary restrictions. Pay a fixed price of €15 to secure your
                  booking.
                </p>
              </div>
              <div className="rounded-xl bg-gray-100 p-6 shadow-lg dark:bg-gray-800 md:w-1/2">
                <h3 className="mb-3 font-medium text-gray-800 dark:text-white">
                  What you'll need to provide:
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Location preference
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Date and time
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Number of guests
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Dietary restrictions
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 2 */}
            <div className="mb-16 md:flex md:flex-row-reverse md:items-center">
              <div className="mb-8 md:mb-0 md:w-1/2 md:pl-12">
                <div className="rounded-full bg-primary/10 p-3 inline-block mb-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
                    2
                  </span>
                </div>
                <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                  Receive Your Restaurant Surprise
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  On the day of your booking, you'll receive details about your
                  surprise restaurant. We'll share the name, address, and any
                  special instructions. The restaurant will be expecting you!
                </p>
              </div>
              <div className="rounded-xl bg-gray-100 p-6 shadow-lg dark:bg-gray-800 md:w-1/2">
                <h3 className="mb-3 font-medium text-gray-800 dark:text-white">
                  What you'll receive:
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Restaurant name and details
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Reservation confirmation
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Directions and maps
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Special instructions (if any)
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="mb-16 md:flex md:items-center">
              <div className="mb-8 md:mb-0 md:w-1/2 md:pr-12">
                <div className="rounded-full bg-primary/10 p-3 inline-block mb-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
                    3
                  </span>
                </div>
                <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                  Enjoy Your Surprise Dining Experience
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  Arrive at the restaurant and enjoy your meal. Show your booking
                  confirmation from SurpriseTable. Your €15 booking fee is credited
                  towards your final bill. You only pay the difference.
                </p>
              </div>
              <div className="rounded-xl bg-gray-100 p-6 shadow-lg dark:bg-gray-800 md:w-1/2">
                <h3 className="mb-3 font-medium text-gray-800 dark:text-white">
                  What to expect:
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Premium dining experience
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    €15 credited toward your bill
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Guaranteed table availability
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Special SurpriseTable treatment
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 4 */}
            <div className="md:flex md:flex-row-reverse md:items-center">
              <div className="mb-8 md:mb-0 md:w-1/2 md:pl-12">
                <div className="rounded-full bg-primary/10 p-3 inline-block mb-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
                    4
                  </span>
                </div>
                <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                  Share Your Experience
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  After your meal, share your experience with us. Your feedback helps
                  us improve our service and recommend better matches for future
                  diners. Refer friends to earn credits for your next surprise.
                </p>
              </div>
              <div className="rounded-xl bg-gray-100 p-6 shadow-lg dark:bg-gray-800 md:w-1/2">
                <h3 className="mb-3 font-medium text-gray-800 dark:text-white">
                  Ways to participate:
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Rate your experience
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Share photos (optional)
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Invite friends and earn €10 credit
                  </li>
                  <li className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Become a regular and get premium perks
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-20 text-center">
            <h2 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
              Ready to Experience Your First Surprise?
            </h2>
            <Link
              href="/public"
              className="inline-block rounded-lg bg-primary px-6 py-3 font-medium text-white transition hover:bg-red-700"
            >
              Book Your Surprise Now
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HowItWorks;
